'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger } from '@/components/ui/popover';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import type { FormOption, InfiniteSelectConfig } from '@/types/base-form';

interface InfiniteComboboxProps {
  /** Selected option value ('' when nothing is selected). */
  value: string;
  onChange: (value: string) => void;
  /** Infinite-scroll config from the `useInfiniteSelect` hook. */
  infiniteConfig: InfiniteSelectConfig;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Label for a pre-selected value not yet in the loaded pages. */
  displayValue?: string;
  /** Re-selecting the current value clears it. Default: false. */
  clearOnReselect?: boolean;
  /**
   * Trailing content rendered inside each option row — e.g. an info popover
   * previewing what the option holds. Returned nodes own their own click
   * handling; stop propagation there to keep the click off the row's selection.
   */
  renderOptionAdornment?: (option: FormOption) => React.ReactNode;
  disabled?: boolean;
  /** Applied to the trigger button. */
  className?: string;
}

/**
 * Standalone (non-react-hook-form) searchable dropdown with server-driven
 * infinite scroll. Mirrors `FormCombobox` but takes `value`/`onChange`
 * directly, for selects driven by local `useState` rather than RHF. Pair with
 * the `useInfiniteSelect` hook so the option list is never capped by `per_page`.
 */
function InfiniteCombobox({
  value,
  onChange,
  infiniteConfig,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  displayValue,
  clearOnReselect = false,
  renderOptionAdornment,
  disabled,
  className
}: InfiniteComboboxProps) {
  const tc = useTranslations('common');
  const [open, setOpen] = useState(false);

  const { options } = infiniteConfig;
  const scrollRef = useRef<HTMLDivElement>(null);
  const fetchGuardRef = useRef(false);

  // Reset the server search when the popover closes.
  useEffect(() => {
    if (!open) {
      infiniteConfig.onSearchChange('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Release the fetch guard once the in-flight page request settles.
  useEffect(() => {
    if (!infiniteConfig.isFetchingNextPage) {
      fetchGuardRef.current = false;
    }
  }, [infiniteConfig.isFetchingNextPage]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || fetchGuardRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (
      scrollHeight - scrollTop - clientHeight < 80 &&
      infiniteConfig.hasNextPage
    ) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [infiniteConfig]);

  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [open, handleScroll]);

  // If everything loaded fits without scrolling, pull the next page so the
  // user doesn't have to scroll into empty space to trigger loading.
  useEffect(() => {
    if (
      !open ||
      infiniteConfig.isLoading ||
      infiniteConfig.isFetchingNextPage ||
      !infiniteConfig.hasNextPage
    ) {
      return;
    }
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight + 1 && !fetchGuardRef.current) {
      fetchGuardRef.current = true;
      infiniteConfig.fetchNextPage();
    }
  }, [open, infiniteConfig, options.length]);

  const selectedLabel =
    displayValue ?? options.find((o) => o.value === value)?.label;
  const selectedColor = options.find((o) => o.value === value)?.color;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'border-input h-9 w-full justify-between bg-transparent px-3 font-normal shadow-xs',
            !value && 'text-muted-foreground',
            className
          )}
        >
          <span className='flex min-w-0 items-center gap-2 truncate'>
            {value && selectedColor && (
              <span
                aria-hidden='true'
                className='h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10'
                style={{ backgroundColor: selectedColor }}
              />
            )}
            <span className='truncate'>
              {value ? (selectedLabel ?? value) : (placeholder ?? tc('search'))}
            </span>
          </span>
          <ChevronsUpDown className='text-muted-foreground ms-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverPrimitive.Content
        align='start'
        sideOffset={4}
        className='bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-(--radix-popover-trigger-width) origin-(--radix-popover-content-transform-origin) rounded-md border p-0 shadow-md outline-hidden'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? tc('search')}
            value={infiniteConfig.search}
            onValueChange={infiniteConfig.onSearchChange}
          />
          <div
            ref={scrollRef}
            className='max-h-[300px] overflow-y-auto'
            onScroll={handleScroll}
          >
            <CommandList className='max-h-none overflow-visible'>
              {infiniteConfig.isLoading ? (
                <div className='flex items-center justify-center py-6'>
                  <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                </div>
              ) : (
                <>
                  <CommandEmpty>{emptyMessage ?? tc('noResults')}</CommandEmpty>
                  <CommandGroup>
                    {options.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        onSelect={(val) => {
                          onChange(clearOnReselect && val === value ? '' : val);
                          setOpen(false);
                        }}
                      >
                        {option.color && (
                          <span
                            aria-hidden='true'
                            className='me-2 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/10'
                            style={{ backgroundColor: option.color }}
                          />
                        )}
                        <span className='flex-1 truncate'>{option.label}</span>
                        {renderOptionAdornment?.(option)}
                        <Check
                          className={cn(
                            'ms-auto h-4 w-4 shrink-0',
                            value === option.value ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  {infiniteConfig.isFetchingNextPage && (
                    <div className='flex items-center justify-center py-2'>
                      <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                    </div>
                  )}
                </>
              )}
            </CommandList>
          </div>
        </Command>
      </PopoverPrimitive.Content>
    </Popover>
  );
}

export { InfiniteCombobox };
